import React, { useState } from 'react';
import { X, Sparkles, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

// Available genres - same as Feed.js
const AVAILABLE_GENRES = [
  'Action', 'Adventure', 'Comedy', 'Drama', 'Fantasy', 'Horror',
  'Romance', 'Sci-Fi', 'Slice of Life', 'Thriller', 'Mystery',
  'Historical', 'Sports', 'Supernatural', 'Urban', 'Music'
];

// Content types
const CONTENT_TYPES = [
  { id: 'comic', label: '📚 Comics', description: 'Visual storytelling' },
  { id: 'art', label: '🎨 Artwork', description: 'Illustrations & designs' },
  { id: 'music', label: '🎵 Music', description: 'Audio tracks & beats' }
];

const OnboardingGenreModal = ({ isOpen, onComplete, onSkip }) => {
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState(['comic', 'art', 'music']);
  const [step, setStep] = useState(1); // 1 = content types, 2 = genres

  const toggleGenre = (genre) => {
    if (selectedGenres.includes(genre)) {
      setSelectedGenres(selectedGenres.filter(g => g !== genre));
    } else {
      if (selectedGenres.length < 8) {
        setSelectedGenres([...selectedGenres, genre]);
      }
    }
  };

  const toggleType = (typeId) => {
    if (selectedTypes.includes(typeId)) {
      // Don't allow empty selection
      if (selectedTypes.length > 1) {
        setSelectedTypes(selectedTypes.filter(t => t !== typeId));
      }
    } else {
      setSelectedTypes([...selectedTypes, typeId]);
    }
  };

  const handleComplete = () => {
    onComplete(selectedGenres, selectedTypes);
  };

  const handleSkip = () => {
    onSkip();
  };

  const handleNext = () => {
    if (step === 1 && selectedTypes.length > 0) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-orange-500" />
              <DialogTitle className="text-2xl font-bold">Welcome to Trap Comix!</DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-gray-400 text-base">
            Let's personalize your feed so you see content you'll love
          </DialogDescription>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center gap-2 mb-6">
          <div className={`flex-1 h-2 rounded-full ${step >= 1 ? 'bg-orange-500' : 'bg-gray-700'}`} />
          <div className={`flex-1 h-2 rounded-full ${step >= 2 ? 'bg-orange-500' : 'bg-gray-700'}`} />
        </div>

        {/* Step 1: Content Types */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-bold mb-2">What type of content do you enjoy?</h3>
              <p className="text-sm text-gray-400 mb-4">Select all that apply</p>
              
              <div className="grid gap-3">
                {CONTENT_TYPES.map((type) => {
                  const isSelected = selectedTypes.includes(type.id);
                  return (
                    <button
                      key={type.id}
                      onClick={() => toggleType(type.id)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        isSelected
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-bold text-lg mb-1">{type.label}</div>
                          <div className="text-sm text-gray-400">{type.description}</div>
                        </div>
                        {isSelected && (
                          <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-800">
              <Button
                onClick={handleSkip}
                variant="ghost"
                className="text-gray-400 hover:text-white"
              >
                I'll do this later
              </Button>
              <Button
                onClick={handleNext}
                disabled={selectedTypes.length === 0}
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold"
              >
                Next Step
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Genres */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-bold mb-2">Pick your favorite genres</h3>
              <p className="text-sm text-gray-400 mb-4">
                Choose up to 8 genres you enjoy {selectedGenres.length > 0 && `(${selectedGenres.length}/8 selected)`}
              </p>
              
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_GENRES.map((genre) => {
                  const isSelected = selectedGenres.includes(genre);
                  const isDisabled = !isSelected && selectedGenres.length >= 8;
                  
                  return (
                    <button
                      key={genre}
                      onClick={() => !isDisabled && toggleGenre(genre)}
                      disabled={isDisabled}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                          : isDisabled
                          ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      {genre}
                      {isSelected && <X className="w-3 h-3 ml-1 inline" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected Summary */}
            {selectedGenres.length > 0 && (
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-orange-500 mt-0.5" />
                  <div>
                    <h4 className="font-bold mb-1">Your personalized feed will include:</h4>
                    <p className="text-sm text-gray-400">
                      {selectedTypes.map(t => {
                        const type = CONTENT_TYPES.find(ct => ct.id === t);
                        return type?.label.split(' ')[1];
                      }).join(', ')} content from genres: {selectedGenres.join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-gray-800">
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleBack}
                  variant="ghost"
                  className="text-gray-400 hover:text-white"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSkip}
                  variant="ghost"
                  className="text-gray-400 hover:text-white"
                >
                  Skip for now
                </Button>
              </div>
              <Button
                onClick={handleComplete}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold"
              >
                {selectedGenres.length > 0 ? 'Complete Setup' : 'Skip Genres'}
              </Button>
            </div>
          </div>
        )}

        {/* Optional: Tip at bottom */}
        <div className="text-xs text-center text-gray-500 mt-4">
          💡 You can always change these preferences later in your Feed page
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingGenreModal;
